import { useMemo } from "react";
import { useReportingHubFilterAssets } from "./useReportingHubFilterAssets";
import { useTenantStore } from "../stores/tenantStore";
import {
	applyReportingHubFilters,
	buildLookupMaps,
	buildOptions,
	cascadeOperationChange,
	cascadeOperatorChange,
	cascadeProjectChange,
	cascadeWellChange,
	ReportingHubFilterSelection,
} from "../utils/reporting/reportingHubFilters";
import { isStreamParamValue } from "../utils/reporting/reportStream";

export type ReportItem = {
	_id: string;
	status?: string;
	open_status?: string;
	internal_report?: boolean;
	operator?: string;
	project?: string;
	well?: string;
	operation?: string;
};

// How a change to each filter carries over to the others.
const CASCADES = {
	operators: cascadeOperatorChange,
	projects: cascadeProjectChange,
	wells: cascadeWellChange,
	operations: cascadeOperationChange,
};

export type CascadeFilter = keyof typeof CASCADES;

// The options a stream's address can keep: a legacy report's stored text,
// such as a well, can hold the comma that splits the address's values.
const toStreamOptions = (options: { label: string; value: string }[]) =>
	options.filter((option) => isStreamParamValue(option.value));

// The listed reports' projects, wells and operations by id, as the filters
// load them to name their options.
export type ReportStreamAssets = {
	projectMap: Record<string, any>;
	wellMap: Record<string, any>;
	operationMap: Record<string, any>;
};

// The operator, project, well and operation filters over a stream's reports,
// for a selection the caller keeps: picking one narrows the options of the
// others, and the selection follows (utils/reporting/reportingHubFilters).
// onChange also gets the filter that was changed. With hasInternalExternal, a
// tenant other than the master tenant doesn't see other tenants' internal
// reports.
export function useReportCascadeFilters<T extends ReportItem>(
	data: T[] | undefined,
	currentTenantKey: string | undefined,
	isMasterTenant: boolean | undefined,
	hasInternalExternal: boolean,
	selection: ReportingHubFilterSelection,
	onChange: (next: ReportingHubFilterSelection, changed: CascadeFilter) => void
) {
	const tenantList = useTenantStore((state) => state.tenantList);
	const { operators, projects, wells, operations } = selection;

	const visibleData = useMemo(() => {
		if (!data) return [];
		return data.filter((item) => {
			if (
				hasInternalExternal &&
				item.internal_report === true &&
				!isMasterTenant &&
				item.operator !== currentTenantKey
			) {
				return false;
			}
			return true;
		});
	}, [data, currentTenantKey, isMasterTenant, hasInternalExternal]);

	const lookupMaps = useMemo(
		() => buildLookupMaps(visibleData as any[]),
		[visibleData]
	);

	const {
		projectMap,
		wellMap,
		operationMap,
		isLoading: isLoadingAssets,
	} = useReportingHubFilterAssets(visibleData as any[], currentTenantKey ?? "");
	// For a stream's search and the line under each title, which name them.
	const assets = useMemo<ReportStreamAssets>(
		() => ({ projectMap, wellMap, operationMap }),
		[projectMap, wellMap, operationMap]
	);

	const applyChange = (field: CascadeFilter, nextValues: string[]) =>
		onChange(CASCADES[field](nextValues, selection, lookupMaps), field);

	const handleOperatorChange = (values: string[]) =>
		applyChange("operators", values);
	const handleProjectChange = (values: string[]) =>
		applyChange("projects", values);
	const handleWellChange = (values: string[]) => applyChange("wells", values);
	const handleOperationChange = (values: string[]) =>
		applyChange("operations", values);

	const operatorOptions = useMemo(() => {
		const ids = [
			...new Set(visibleData.map((item) => item.operator).filter(Boolean)),
		] as string[];
		return ids
			.map((id) => ({
				label:
					tenantList.find((tenant: any) => tenant.role_key === id)?.name ?? id,
				value: id,
			}))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [visibleData, tenantList]);

	const projectOptions = useMemo(
		() =>
			toStreamOptions(
				buildOptions(
					applyReportingHubFilters(visibleData as any[], { operators }),
					"project",
					projectMap,
					"project_name"
				)
			),
		[visibleData, operators, projectMap]
	);

	const wellOptions = useMemo(
		() =>
			toStreamOptions(
				buildOptions(
					applyReportingHubFilters(visibleData as any[], {
						operators,
						projects,
					}),
					"well",
					wellMap,
					"well_name"
				)
			),
		[visibleData, operators, projects, wellMap]
	);

	const operationOptions = useMemo(
		() =>
			toStreamOptions(
				buildOptions(
					applyReportingHubFilters(visibleData as any[], {
						operators,
						projects,
						wells,
					}),
					"operation",
					operationMap,
					"operation_name"
				)
			),
		[visibleData, operators, projects, wells, operationMap]
	);

	// The reports the four filters leave, before any status filter.
	const cascadedData = useMemo(
		() =>
			applyReportingHubFilters(visibleData as any[], {
				operators,
				projects,
				wells,
				operations,
			}) as T[],
		[visibleData, operators, projects, wells, operations]
	);

	return {
		visibleData,
		cascadedData,
		handleOperatorChange,
		handleProjectChange,
		handleWellChange,
		handleOperationChange,
		operatorOptions,
		projectOptions,
		wellOptions,
		operationOptions,
		isLoadingAssets,
		assets,
	};
}
